import { ArrowRightIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Badge } from "@/core/components/ui/badge";
import { Button } from "@/core/components/ui/button";
import { SimpleSidebar } from "@/core/components/ui/simple-sidebar";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import type { MemberChange } from "./utils";
import { formatRosterValue } from "./utils";

interface RosterChangesPanelProps {
  changes: MemberChange[];
  publishing: boolean;
  onClose: () => void;
  onPublish: () => void;
}

// The "preview": a push sidebar that opens beside the still-visible roster and
// shows exactly what each edit changes per tech — so the owner sees the impact
// on that person's payroll setup, side by side, before publishing.
export const RosterChangesPanel = ({
  changes,
  publishing,
  onClose,
  onPublish,
}: RosterChangesPanelProps) => {
  const editChanges = changes.filter(
    (change) => change.kind !== "removed" && change.kind !== "dismissed",
  );
  const removedChanges = changes.filter((change) => change.kind === "removed");
  const dismissedChanges = changes.filter(
    (change) => change.kind === "dismissed",
  );

  return (
    <SimpleSidebar aria-label="Review roster changes">
      <Stack gap="xs" className="border-b border-stroke-tertiary p-6">
        <Stack direction="row" align="center" justify="between" fullWidth>
          <Typography variant="subtitleW1">Review roster changes</Typography>
          <Button
            size="icon"
            variant="text"
            aria-label="Close review"
            onClick={onClose}
          >
            <XMarkIcon />
          </Button>
        </Stack>
        <Typography variant="bodyW3" textColor="secondary">
          {changes.length} {changes.length === 1 ? "person" : "people"} will
          change in this new roster version.
        </Typography>
      </Stack>

      <Stack gap="none" className="flex-1 overflow-y-auto p-6">
        <Stack gap="lg" fullWidth>
          {editChanges.length > 0 && (
            <Stack gap="md" fullWidth>
              {editChanges.map((change) => (
                <Stack
                  key={change.id}
                  gap="none"
                  fullWidth
                  className="overflow-hidden rounded-xl border border-stroke-tertiary bg-card"
                >
                  <Stack
                    direction="row"
                    align="center"
                    justify="between"
                    gap="sm"
                    fullWidth
                    className="border-b border-stroke-tertiary px-4 py-3"
                  >
                    <Stack direction="row" align="baseline" gap="sm">
                      <Typography variant="subtitleW3">
                        {change.name || "New person"}
                      </Typography>
                      {change.employeeId && (
                        <Typography variant="bodyW3" textColor="tertiary">
                          #{change.employeeId}
                        </Typography>
                      )}
                    </Stack>
                    {change.kind === "added" && (
                      <Badge variant="green" size="sm">
                        New
                      </Badge>
                    )}
                  </Stack>
                  <Stack gap="sm" fullWidth className="p-4">
                    {change.fields.map((field) => (
                      <Stack
                        key={field.key}
                        direction="row"
                        align="start"
                        justify="between"
                        gap="md"
                        fullWidth
                        className="flex-wrap"
                      >
                        <Typography
                          variant="bodyW3"
                          textColor="tertiary"
                          className="shrink-0 pt-px"
                        >
                          {field.label}
                        </Typography>
                        <Stack
                          direction="row"
                          align="center"
                          justify="end"
                          gap="sm"
                          className="flex-wrap"
                        >
                          {change.kind === "updated" && (
                            <Stack
                              direction="row"
                              align="center"
                              gap="xs"
                              className="text-sm text-tertiary-text"
                            >
                              <span className="line-through">
                                {formatRosterValue(field.before, field.field)}
                              </span>
                              <ArrowRightIcon className="size-3.5 shrink-0" />
                            </Stack>
                          )}
                          <span className="text-sm font-medium text-primary-text">
                            {formatRosterValue(field.after, field.field)}
                          </span>
                        </Stack>
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              ))}
            </Stack>
          )}

          {removedChanges.length > 0 && (
            <Stack gap="sm" fullWidth>
              <Typography
                variant="subtitleW3"
                textColor="tertiary"
                className="uppercase tracking-wide"
              >
                Will be removed
              </Typography>
              {removedChanges.map((change) => (
                <Stack
                  key={change.id}
                  gap="xs"
                  fullWidth
                  className="rounded-xl border border-red-20 bg-red-5 px-4 py-3"
                >
                  <Stack
                    direction="row"
                    align="center"
                    justify="between"
                    gap="sm"
                    fullWidth
                  >
                    <Stack direction="row" align="baseline" gap="sm">
                      <Typography variant="subtitleW3">
                        {change.name || "Unnamed person"}
                      </Typography>
                      {change.employeeId && (
                        <Typography variant="bodyW3" textColor="tertiary">
                          #{change.employeeId}
                        </Typography>
                      )}
                    </Stack>
                    <Badge variant="red" size="sm">
                      Removed
                    </Badge>
                  </Stack>
                  <Typography variant="bodyW3" className="text-red-70">
                    Comes off payroll when this version publishes. They keep
                    their Volca account and their past pay.
                  </Typography>
                </Stack>
              ))}
            </Stack>
          )}

          {dismissedChanges.length > 0 && (
            <Stack gap="sm" fullWidth>
              <Typography
                variant="subtitleW3"
                textColor="tertiary"
                className="uppercase tracking-wide"
              >
                Will stop being suggested
              </Typography>
              {dismissedChanges.map((change) => (
                <Stack
                  key={change.id}
                  gap="xs"
                  fullWidth
                  className="rounded-xl border border-stroke-tertiary px-4 py-3"
                >
                  <Stack direction="row" align="baseline" gap="sm">
                    <Typography variant="subtitleW3">
                      {change.name ||
                        (change.employeeId
                          ? `ServiceTitan employee #${change.employeeId}`
                          : "Dismissed person")}
                    </Typography>
                    {change.name && change.employeeId && (
                      <Typography variant="bodyW3" textColor="tertiary">
                        #{change.employeeId}
                      </Typography>
                    )}
                  </Stack>
                  <Typography variant="bodyW3" textColor="secondary">
                    Marked as not on payroll, so the roster stops offering them.
                    They keep their Volca account so that they can see their
                    past payroll.
                  </Typography>
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>
      </Stack>

      <Stack
        direction="row"
        justify="end"
        gap="sm"
        fullWidth
        className="border-t border-stroke-tertiary p-6"
      >
        <Button variant="secondary" onClick={onClose}>
          Keep editing
        </Button>
        <Button loading={publishing} onClick={onPublish}>
          Publish new version
        </Button>
      </Stack>
    </SimpleSidebar>
  );
};
